import { InlineShippingOptions, ProductInput, ProductOptionInput, ProductType, ProductVariantInput } from '@shopify-multiseller/graphql/partner/types';
import * as yup from 'yup';

// Helper function to determine initial international shipping state
const getInitialInternationalShipping = (inlineShippingOptions: InlineShippingOptions) => {
  return inlineShippingOptions === InlineShippingOptions.InternationalMandatory;
};

export const VariantValidationSchema = yup.object({
  price: yup
    .number()
    .moreThan(0, 'Must be greater than zero')
    .required('Price is required')
    .typeError('Price must be a number'),
  sku: yup.string().when('showSku', {
    is: true,
    then: yup.string().required('SKU is required'),
    otherwise: yup.string().transform(() => ''),
  }),
  trackQuantity: yup.boolean(),
  quantity: yup
    .number()
    .nullable(true)
    .test(
      'quantity-validation',
      function (value) {
        const { path, parent, createError } = this;
        const isIdFieldExists = !!parent.id;

        if (isIdFieldExists) {
          return true;
        } else {
          if (parent.trackQuantity) {
            return (
              (typeof value === 'number' && Number.isInteger(value)) ||
              createError({
                path,
                message: "Quantity must be a number and not empty.",
              })
            );
          } else {
            return (
              (typeof value === 'number' && Number.isInteger(value)) ||
              createError({
                path,
                message: "Quantity must be an integer",
              })
            );
          }
        }
      }
    ),
 
});

export const ProductValidationSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  description: yup.string().required('Description is required'),
  productType: yup.mixed()
    .nullable()
    .required('Product type is required'),
  variants: yup.array().of(VariantValidationSchema),
  standardShipping: yup.mixed().when('shippingType', {
    is: 'standard',
    then: yup.object({
      countryOfOrigin: yup.string()
        .required('Country of origin is required'),
      domesticRate: yup.number()
        .typeError('Domestic shipping rate must be a number')
        .required('Domestic shipping rate is required')
        .min(0, 'Domestic shipping rate must be 0 or greater'),
      internationalRate: yup.number().when('internationalShipping', {
        is: true,
        then: yup.number()
          .typeError('International shipping rate must be a number')
          .required('International shipping rate is required')
          .min(0, 'International shipping rate must be 0 or greater'),
        otherwise: yup.number().nullable()
      })
    }).required('Shipping information is required').default(undefined),
    otherwise: yup.mixed().nullable()
  }),
  shippingType: yup.string().nullable(),
  internationalShipping: yup.boolean()
});

export interface IProductInitialValuesProps extends Omit<ProductInput, 'productType'> {
  id: string;
  variants: (ProductVariantInput & { showSku: boolean, isNew: boolean })[];
  productType?: ProductType;
  allowDeliveryProfile: boolean;
  internationalShipping: boolean;
  shippingType?: "standard" | "profile";

}

export interface IAddProductInitialValuesProps {
  title: string;
  description: string;
  options?: ProductOptionInput[] | null;
  variants: ProductVariantInput[];
}

export interface IVariantInitialValuesProps {
  options?: ProductOptionInput[] | null;
  variant: ProductVariantInput & { showSku: boolean };
}

export type ProductDisplayStatus = 'current' | 'pending';

export type IndetermiableBoolean = boolean | 'indeterminate';
